import { Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";

import { PillSegments } from "@/components/data-display/viz";
import { EmptyState } from "@/components/ui/feedback";
import { cn } from "@/lib/utils";
import type { Approval, ApprovalType } from "@/types";

const TYPE_ICON: Record<ApprovalType, { symbol: string; label: string; bg: string; color: string }> = {
  transfer: { symbol: "↑", label: "transfer", bg: "var(--ads-tint-blue)", color: "var(--ads-info)" },
  wallet: { symbol: "⊞", label: "wallet", bg: "var(--ads-tint-green)", color: "var(--ads-viz-green)" },
  policy: { symbol: "⚙", label: "policy", bg: "var(--ads-warning-soft)", color: "var(--ads-viz-amber-strong)" },
};

const SLA_COLOR: Record<Approval["slaSeverity"], string> = {
  danger: "var(--ads-danger)",
  warn: "var(--ads-warning)",
  ok: "var(--ads-success)",
};

const hrefFor = (approval: Approval) =>
  approval.type === "policy" ? "/governance" : `/approvals/${approval.id}`;

const cleanTitle = (title: string) => title.replace(/^(Transfer|Wallet Creation|Policy Change): /, "");

/**
 * Minutes left on an SLA string ("3h 58m left" → 238), or `null` when the
 * approval carries no SLA text at all.
 *
 * The `null` matters: `mapApiApproval` (`hooks/use-approvals.ts`) leaves
 * `slaLeft` empty because the gateway does not report one yet (ALL-4734 G6), and
 * an approval with no SLA is *unknown*, not "due now" — treating it as 0 minutes
 * would make every approval look like it was about to breach. Days are parsed
 * too, so "2d left" is two days rather than zero minutes.
 */
function slaMinutes(slaLeft: string): number | null {
  if (!slaLeft.trim()) return null;
  const days = Number(/(\d+)\s*d/.exec(slaLeft)?.[1] ?? 0);
  const hours = Number(/(\d+)\s*h/.exec(slaLeft)?.[1] ?? 0);
  const minutes = Number(/(\d+)\s*m/.exec(slaLeft)?.[1] ?? 0);
  return days * 1440 + hours * 60 + minutes;
}

/** SLA chip. The full "4h left" text is kept, and is the chip's own text node. */
function SlaPill({ approval }: { approval: Approval }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
        approval.slaSeverity === "danger"
          ? "bg-danger-soft text-danger"
          : approval.slaSeverity === "warn"
            ? "bg-warning-soft text-warning"
            : "bg-success-soft text-success"
      )}
    >
      <Clock aria-hidden className="size-3" />
      {approval.slaLeft}
    </span>
  );
}

/** Quorum as pips: filled = approvals given, hollow = still needed. */
function Quorum({
  done,
  required,
  className,
}: {
  done: number;
  required: number;
  className?: string;
}) {
  // A quorum of zero is not a quorum: render nothing rather than "0/0".
  if (!Number.isFinite(required) || required <= 0) return null;
  const filled = Math.max(0, Math.min(done, required));
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      aria-label={`${done} of ${required} approvals`}
    >
      <span className="flex gap-0.5">
        {Array.from({ length: required }, (_, index) => (
          <span
            key={index}
            className={cn(
              "size-2 rounded-full border",
              index < filled ? "border-success bg-success" : "border-line-strong bg-surface"
            )}
          />
        ))}
      </span>
      <span className="text-[11px] tabular-nums text-muted">
        {done}/{required}
      </span>
    </span>
  );
}

/** The most urgent approval of the queue, as the tile's "next up" card. */
function NextUpRow({ approval }: { approval: Approval }) {
  const type = TYPE_ICON[approval.type];
  return (
    <Link
      to={hrefFor(approval)}
      className={cn(
        "mb-3 block rounded-xl border p-3.5 transition-colors hover:border-primary",
        approval.slaSeverity === "warn"
          ? "border-warning-border bg-warning-soft/40"
          : approval.slaSeverity === "danger"
            ? "border-danger-border bg-danger-soft/40"
            : "border-line bg-bg"
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
          {approval.urgent ? "Urgent · next up" : "Next up"}
        </span>
        {approval.slaLeft && <SlaPill approval={approval} />}
      </div>
      <div className="flex items-start gap-2.5">
        <span
          className="grid size-8 shrink-0 place-items-center rounded-full text-[15px] font-bold leading-none"
          style={{ background: type.bg, color: type.color }}
        >
          {type.symbol}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-snug text-ink">
            {cleanTitle(approval.title)}
          </div>
          {approval.meta && <div className="mt-0.5 truncate text-xs text-muted">{approval.meta}</div>}
          {approval.hint && <div className="mt-1.5 text-xs text-muted">{approval.hint}</div>}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <Quorum done={approval.approvalsDone} required={approval.approvalsRequired} />
        <span className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-on-primary">
          Review now →
        </span>
      </div>
    </Link>
  );
}

/** A queue row: the same body the tile has always rendered, plus its quorum. */
function ApprovalRow({ approval }: { approval: Approval }) {
  const type = TYPE_ICON[approval.type];
  return (
    <Link
      to={hrefFor(approval)}
      className="flex items-start gap-3 rounded-lg border border-line bg-bg px-3 py-2 transition-colors hover:border-primary hover:bg-surface"
    >
      <span
        className="flex-shrink-0 rounded-[4px] px-2 py-[3px] text-[15px] font-bold leading-none"
        style={{ background: type.bg, color: type.color }}
      >
        {type.symbol}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-ink">{cleanTitle(approval.title)}</div>
        {approval.meta && <div className="mt-0.5 truncate text-xs text-muted">{approval.meta}</div>}
        {approval.hint && <div className="mt-1.5 text-xs text-muted">{approval.hint}</div>}
        <Quorum className="mt-1.5" done={approval.approvalsDone} required={approval.approvalsRequired} />
      </div>
      {approval.slaLeft && <SlaPill approval={approval} />}
    </Link>
  );
}

export function PendingApprovals({
  approvals,
  allApprovals,
}: {
  /** The rows to render. The dashboard caps this at the queue limit. */
  approvals: Approval[];
  /**
   * Every pending approval, when `approvals` is a display-limited slice.
   *
   * The summary figures — the "N waiting" count, the SLA maths and the
   * per-type chips — describe the queue, not the five rows that fit on the
   * tile, so they are computed from this. Counting off the sliced list would
   * print "5 waiting" while twelve were pending. Defaults to `approvals` for
   * callers that already pass the full queue.
   */
  allApprovals?: Approval[];
}) {
  // Everything the summary states is a statement about the whole queue.
  const queue = allApprovals ?? approvals;

  if (queue.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Pending approvals</h2>
        <EmptyState
          tone="positive"
          className="flex-1 justify-center"
          icon={<CheckCircle2 aria-hidden className="size-6 text-success" />}
          title="All caught up"
          description="No approvals are waiting on you."
        />
      </div>
    );
  }

  // Queue order is the selector's job (`getPendingApprovals` sorts urgent first,
  // then by severity); the tile only decides which row is "next up", so it never
  // reorders the list the rest of the dashboard is already sorted on.
  const [next, ...rest] = approvals;

  // SLA statements are made about the approvals that actually carry an SLA
  // target. The gateway reports none yet, so the tile says that instead of
  // claiming everything is "on track" (or that everything is about to breach).
  const slaTracked = queue.filter((a) => slaMinutes(a.slaLeft) !== null);
  const dueSoon = slaTracked.filter((a) => (slaMinutes(a.slaLeft) ?? 0) < 60).length;
  // "On track" is the approvals whose own severity is ok — not "everything that
  // is not due within the hour". A tracked approval carrying a warn/danger
  // severity is on the watch list even when its SLA text is further out, and
  // counting it as on-track would contradict the severity the row shows.
  const onTrack = slaTracked.filter((a) => a.slaSeverity === "ok").length;
  const withoutSla = queue.length - slaTracked.length;

  const byType = (["transfer", "wallet", "policy"] as ApprovalType[])
    .map((type) => ({ type, count: queue.filter((a) => a.type === type).length }))
    .filter((entry) => entry.count > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Pending approvals</h2>
        <Link to="/approvals" className="text-xs font-semibold text-primary hover:underline">
          View all →
        </Link>
      </div>

      <div className="mb-4">
        <div className="mb-2 flex items-end justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[30px] font-semibold leading-none tabular-nums text-ink">
              {queue.length}
            </span>
            <span className="text-sm text-muted">waiting</span>
          </div>
          <span className="text-right text-[11px] leading-tight text-muted">
            {slaTracked.length === 0 ? (
              "SLA targets are not available for these approvals."
            ) : (
              <>
                {dueSoon > 0 ? (
                  <span className="font-semibold text-warning">{dueSoon} due within 1h</span>
                ) : (
                  "None due within 1h"
                )}
                <br />
                {onTrack} on track
                {withoutSla > 0 && <> · {withoutSla} without an SLA target</>}
              </>
            )}
          </span>
        </div>
        {slaTracked.length > 0 && (
          <PillSegments
            label={`${slaTracked.length} approvals with an SLA target: ${dueSoon} due within an hour`}
            segments={slaTracked.map((a) => ({
              key: a.id,
              color: SLA_COLOR[a.slaSeverity],
              title: `${a.id} · ${a.slaLeft}`,
            }))}
          />
        )}
      </div>

      <NextUpRow approval={next} />

      {rest.length > 0 && (
        <ul className="flex flex-col divide-y divide-line">
          {rest.map((a) => (
            <li key={a.id}>
              <ApprovalRow approval={a} />
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line pt-3">
        {byType.map(({ type, count }) => {
          const meta = TYPE_ICON[type];
          return (
            <span
              key={type}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2 py-0.5 text-[11px] text-muted"
            >
              <span
                className="grid size-4 place-items-center rounded-full text-[9px] font-bold leading-none"
                style={{ background: meta.bg, color: meta.color }}
              >
                {meta.symbol}
              </span>
              <span className="text-ink">{count}</span> {meta.label}
              {count === 1 ? "" : "s"}
            </span>
          );
        })}
      </div>
    </div>
  );
}
